// Sprint 3 Story 4: Magic-Link-Einlöser für Externe.
// Zwei Schritte seit Migration 0074: (1) Token aus ?token= an /api/external/login
// senden → Server mailt einen 6-stelligen Code an die eingeladene Adresse;
// (2) Code eingeben → /api/external/verify tauscht Token+Code gegen die Session
// und redirected ins Projekt bzw. auf die Gast-Aufgabenseite.

import { useEffect, useRef, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { refreshMe } from "../lib/auth";

type Phase = "checking" | "code" | "verifying";

export function ExternalLogin(): preact.JSX.Element {
  const [, setLocation] = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("checking");
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const tokenRef = useRef<string | null>(null);

  const requestCode = async (): Promise<void> => {
    const token = tokenRef.current;
    if (!token) return;
    const res = await fetch("/api/external/login", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(data.error ?? `Fehler ${res.status}`);
    }
    const data = (await res.json()) as { otp_required: boolean; email?: string };
    setMaskedEmail(data.email ?? null);
  };

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setError("Kein Token in der URL — Einladungslink unvollständig.");
      return;
    }
    tokenRef.current = token;
    void requestCode()
      .then(() => {
        if (!cancelled) setPhase("code");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Login fehlgeschlagen");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const verify = async (): Promise<void> => {
    const token = tokenRef.current;
    const trimmed = code.trim();
    if (!token || !/^\d{6}$/.test(trimmed)) {
      setCodeError("Bitte den 6-stelligen Code eingeben.");
      return;
    }
    setPhase("verifying");
    setCodeError(null);
    try {
      const res = await fetch("/api/external/verify", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, code: trimmed }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
          otp_expired?: boolean;
        };
        if (data.otp_expired) {
          setCodeError(
            "Der Code ist abgelaufen oder gesperrt. Fordere unten einen neuen Code an."
          );
        } else {
          setCodeError(data.error === "Invalid code" ? "Falscher Code." : (data.error ?? `Fehler ${res.status}`));
        }
        setPhase("code");
        return;
      }
      const data = (await res.json()) as {
        project_id: number;
        task_id: number | null;
        bearer?: string;
      };
      // Falls Cookie aus 3rd-party-Gründen nicht greift, Bearer als Fallback.
      if (data.bearer) {
        try {
          localStorage.setItem("juuplan:teams_bearer", data.bearer);
        } catch {
          // Storage may be unavailable (private mode, quota) — the cookie set
          // by the server is the primary auth path, this is only a fallback.
        }
      }
      // Die App hat beim Laden der /external-Seite bereits ein /api/me → 401
      // gecacht (noch keine Session) und refetcht das nicht von allein. Ohne
      // Neuladen sieht das Layout weiterhin user:null und wirft den Gast zurück
      // auf /login → Endlosschleife. Deshalb erst me neu laden, dann navigieren.
      await refreshMe();
      // URL putzen und navigieren: task-skopierter Gast → Minimal-Seite,
      // sonst die bestehende Projekt-SPA.
      window.history.replaceState({}, "", "/");
      if (data.task_id != null) {
        setLocation("/guest-task");
      } else {
        setLocation(`/p/${data.project_id}`);
      }
    } catch {
      setCodeError("Verbindung fehlgeschlagen — bitte erneut versuchen.");
      setPhase("code");
    }
  };

  const resend = async (): Promise<void> => {
    setResent(false);
    setCodeError(null);
    try {
      await requestCode();
      setResent(true);
    } catch (err) {
      setCodeError(err instanceof Error ? err.message : "Neuversand fehlgeschlagen");
    }
  };

  return (
    <main class="min-h-dvh flex items-center justify-center p-6 bg-[color:var(--color-surface)]">
      <div class="max-w-md w-full rounded-lg bg-[color:var(--color-surface-container-lowest)] shadow-level-2 border border-[color:var(--color-outline-variant)] p-6 text-center">
        <h1 class="text-2xl font-bold text-[color:var(--color-on-surface)]">juuplan</h1>
        {error ? (
          <>
            <p class="mt-3 text-sm text-[color:var(--color-error)]">{error}</p>
            <p class="mt-3 text-xs text-[color:var(--color-on-surface-variant)]">
              Anmelde-Links sind einmalig nutzbar — dieser wurde vermutlich
              schon verwendet oder ersetzt. Fordere auf der Anmeldeseite mit
              deiner E-Mail-Adresse einfach einen neuen an.
            </p>
            <a
              href="/login"
              class="mt-3 inline-block rounded-md bg-[color:var(--color-primary)] px-4 py-2 text-sm font-medium text-[color:var(--color-on-primary)]"
            >
              Zur Anmeldung
            </a>
          </>
        ) : phase === "checking" ? (
          <p class="mt-3 text-sm text-[color:var(--color-on-surface-variant)]">
            Einladung wird geprüft…
          </p>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void verify();
            }}
          >
            <p class="mt-3 text-sm text-[color:var(--color-on-surface-variant)]">
              Wir haben einen 6-stelligen Anmeldecode an{" "}
              <strong class="text-[color:var(--color-on-surface)]">
                {maskedEmail ?? "deine E-Mail-Adresse"}
              </strong>{" "}
              geschickt.
            </p>
            <input
              type="text"
              inputMode="numeric"
              autocomplete="one-time-code"
              maxLength={6}
              value={code}
              autoFocus
              disabled={phase === "verifying"}
              onInput={(e) =>
                setCode((e.target as HTMLInputElement).value.replace(/\D/g, ""))
              }
              placeholder="000000"
              aria-label="Anmeldecode"
              class="mt-4 w-40 rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-center text-base sm:text-lg tracking-[0.4em] font-mono text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:opacity-60"
            />
            {codeError && (
              <p class="mt-2 text-sm text-[color:var(--color-error)]" role="alert">
                {codeError}
              </p>
            )}
            {resent && !codeError && (
              <p class="mt-2 text-sm text-[color:var(--color-status-done)]" role="status">
                Neuer Code verschickt — bitte Postfach prüfen.
              </p>
            )}
            <div class="mt-4 flex flex-col items-center gap-2">
              <button
                type="submit"
                disabled={phase === "verifying" || code.length !== 6}
                class="w-40 rounded-md bg-[color:var(--color-primary)] px-4 py-2 text-sm font-medium text-[color:var(--color-on-primary)] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
              >
                {phase === "verifying" ? "Prüfe…" : "Anmelden"}
              </button>
              <button
                type="button"
                onClick={() => void resend()}
                disabled={phase === "verifying"}
                class="text-xs text-[color:var(--color-primary)] underline-offset-2 hover:underline disabled:opacity-50"
              >
                Code erneut senden
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
